import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Loader2, Megaphone, ShieldAlert } from 'lucide-react';
import { toast } from 'sonner';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import {
  useCampaignDetailQuery,
  useSegmentListQuery,
} from '@/client/api/campaigns/lifecycle.query';
import {
  useCreateCampaignMutation,
  useUpdateCampaignMutation,
} from '@/client/api/campaigns/lifecycle.mutation';
import {
  CreateCampaignPayload,
  LifecycleChannel,
  segmentId,
} from '@/client/types/campaigns';
import { useBreadcrumbs } from '@/client/hooks/use-breadcrumbs';
import { useMailboxesQuery } from '@/client/api/inbox/inbox';
import { MultiSelectFilter } from '@/client/components/admin/MultiSelectFilter';
import { useTemplateListQuery } from '@/client/api/campaigns/campaigns.query';
import { EmailFrame } from '@/client/components/admin/Campaigns/TemplateDialogs';
import { useHasPermission } from '@/client/hooks/useHasPermission';
import { generateIdempotencyKey } from '@/client/utils/requestSecurity';

const CHANNELS: { id: LifecycleChannel; label: string }[] = [
  { id: 'email', label: 'Email' },
  { id: 'sms', label: 'SMS' },
  { id: 'whatsapp', label: 'WhatsApp' },
  { id: 'in_app', label: 'In-app' },
];

const AccessDenied = () => (
  <div className="flex flex-col items-center justify-center gap-3 rounded-xl border border-gray-100 bg-white py-20 text-center shadow-sm">
    <div className="flex h-14 w-14 items-center justify-center rounded-full bg-red-50">
      <ShieldAlert className="h-7 w-7 text-red-500" />
    </div>
    <h2 className="text-lg font-semibold text-gray-800">Access restricted</h2>
    <p className="max-w-sm text-sm text-gray-500">
      Requires <code className="rounded bg-gray-100 px-1.5 py-0.5 text-xs">campaign.create</code>.
    </p>
  </div>
);

const CampaignEditorPage = () => {
  const { cid } = useParams<{ cid?: string }>();
  const isEdit = Boolean(cid);
  const navigate = useNavigate();

  useBreadcrumbs([
    { label: 'Campaigns', href: '/admin/relationship/campaigns' },
    { label: isEdit ? 'Edit' : 'New campaign' },
  ]);

  const createPerm = useHasPermission('campaign.create');
  const canCreate = createPerm.isSuperAdmin || createPerm.hasPermission;
  const permLoading = createPerm.isLoading;

  const detailQ = useCampaignDetailQuery(cid ?? null, isEdit && canCreate && !permLoading);
  const segmentsQ = useSegmentListQuery(canCreate && !permLoading);

  const create = useCreateCampaignMutation();
  const update = useUpdateCampaignMutation();
  const templatesQ = useTemplateListQuery({}, canCreate && !permLoading);
  const emailTemplates = (templatesQ.data?.templates ?? [])
    .filter((t) => t.email_html || t.email_text)
    .sort((a, b) => b.ntmplid - a.ntmplid);

  const [name, setName] = useState('');
  const [channels, setChannels] = useState<LifecycleChannel[]>(['email']);
  const [subject, setSubject] = useState('');
  // Launches and newsletters go out from marketing@ so replies reach the admin inbox.
  const [fromEmail, setFromEmail] = useState('marketing@feelyfeely.com');
  const [addSignature, setAddSignature] = useState(true);
  const mailboxes = useMailboxesQuery().data || [];
  const [body, setBody] = useState('');
  const [html, setHtml] = useState('');
  const [waTemplate, setWaTemplate] = useState('');
  const [waLanguage, setWaLanguage] = useState('en_US');
  const [segmentIds, setSegmentIds] = useState<string[]>([]);
  const [excludeIds, setExcludeIds] = useState<string[]>([]);
  const [adHoc, setAdHoc] = useState('');
  const [respectPrefs, setRespectPrefs] = useState(true);
  const [rateLimit, setRateLimit] = useState('600');
  const [hydrated, setHydrated] = useState(!isEdit);

  useEffect(() => {
    if (!isEdit || !detailQ.data || hydrated) return;
    const c = detailQ.data;
    if (c.status !== 'draft') {
      toast.error('Only drafts can be edited', { richColors: true });
      navigate(`/admin/relationship/campaigns/${c.campaign_id}`, { replace: true });
      return;
    }
    setName(c.name || '');
    setChannels((c.channels as LifecycleChannel[]) || ['email']);
    setSubject(c.content?.subject || '');
    setFromEmail(c.content?.from_email || '');
    setAddSignature(c.content?.add_signature !== false);
    setBody(c.content?.body || '');
    setHtml(c.content?.html || '');
    setWaTemplate(c.content?.whatsapp?.template_name || '');
    setWaLanguage(c.content?.whatsapp?.language || 'en_US');
    setSegmentIds(c.audience?.segment_ids || []);
    setExcludeIds(c.audience?.exclude_segment_ids || []);
    setAdHoc(
      (c.audience?.ad_hoc || [])
        .map((r) =>
          r.uid != null ? `uid:${r.uid}` : r.email ? r.email : r.phone || '',
        )
        .filter(Boolean)
        .join('\n'),
    );
    setRespectPrefs(c.respect_prefs !== false);
    setRateLimit(String(c.rate_limit_per_min ?? 600));
    setHydrated(true);
  }, [detailQ.data, hydrated, isEdit, navigate]);

  const toggleChannel = (id: LifecycleChannel) => {
    setChannels((prev) =>
      prev.includes(id) ? prev.filter((c) => c !== id) : [...prev, id],
    );
  };

  const buildPayload = (): CreateCampaignPayload | null => {
    if (!name.trim()) {
      toast.error('Name is required', { richColors: true });
      return null;
    }
    if (channels.length === 0) {
      toast.error('Select at least one channel', { richColors: true });
      return null;
    }
    if (!body.trim() && !waTemplate.trim()) {
      toast.error('Body or WhatsApp template is required', { richColors: true });
      return null;
    }

    const ad_hoc = adHoc
      .split(/[\n,]+/)
      .map((l) => l.trim())
      .filter(Boolean)
      .map((line): { uid?: number; email?: string; phone?: string } | null => {
        const lower = line.toLowerCase();
        if (lower.startsWith('uid:')) {
          const uid = Number(line.slice(4).trim());
          return Number.isFinite(uid) && uid > 0 ? { uid } : null;
        }
        if (line.includes('@')) return { email: line };
        if (/^\+?\d[\d\s-]+$/.test(line)) return { phone: line.replace(/\s+/g, '') };
        const uid = Number(line);
        return Number.isFinite(uid) && uid > 0 ? { uid } : null;
      })
      .filter((r): r is { uid?: number; email?: string; phone?: string } => r != null);

    if (segmentIds.length === 0 && ad_hoc.length === 0) {
      toast.error('Add a segment or ad-hoc recipients', { richColors: true });
      return null;
    }

    return {
      name: name.trim(),
      channels,
      content: {
        ...(subject.trim() ? { subject: subject.trim() } : {}),
        ...(body.trim() ? { body: body.trim() } : {}),
        ...(html.trim() ? { html: html.trim() } : {}),
        ...(channels.includes('email') && fromEmail ? { from_email: fromEmail, add_signature: addSignature } : {}),
        ...(channels.includes('whatsapp') && waTemplate.trim()
          ? {
              whatsapp: {
                template_name: waTemplate.trim(),
                language: waLanguage.trim() || 'en_US',
                components: [],
              },
            }
          : {}),
      },
      audience: {
        segment_ids: segmentIds,
        ad_hoc,
        exclude_segment_ids: excludeIds.filter((x) => !segmentIds.includes(x)),
      },
      respect_prefs: respectPrefs,
      schedule: { mode: 'now' },
      rate_limit_per_min: Number(rateLimit) || 600,
    };
  };

  const handleSave = async () => {
    const bodyPayload = buildPayload();
    if (!bodyPayload) return;
    try {
      if (isEdit && cid) {
        const updated = await update.mutateAsync({
          cid,
          body: bodyPayload,
          idempotencyKey: generateIdempotencyKey(),
        });
        toast.success('Draft updated', { richColors: true });
        navigate(`/admin/relationship/campaigns/${updated.campaign_id || cid}`);
      } else {
        const created = await create.mutateAsync({
          body: bodyPayload,
          idempotencyKey: generateIdempotencyKey(),
        });
        toast.success('Draft created', { richColors: true });
        navigate(`/admin/relationship/campaigns/${created.campaign_id}`);
      }
    } catch (error: any) {
      toast.error(error?.message || 'Save failed', { richColors: true });
    }
  };

  if (permLoading || (isEdit && !hydrated && detailQ.isLoading)) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="mx-auto max-w-3xl space-y-4">
          <Skeleton className="h-10 w-48" />
          <Skeleton className="h-64 w-full" />
        </div>
      </div>
    );
  }

  if (!canCreate) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="mx-auto max-w-3xl">
          <AccessDenied />
        </div>
      </div>
    );
  }

  const segments = segmentsQ.data?.segments ?? [];
  const segmentOptions = segments.map((sg) => ({
    key: segmentId(sg),
    label: `${sg.name}${sg.estimated_size != null ? ` (${sg.estimated_size.toLocaleString()})` : ''}`,
  }));
  const saving = create.isPending || update.isPending;

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="mx-auto max-w-3xl space-y-6">
        <div>
          <Link
            to="/admin/relationship/campaigns"
            className="mb-2 inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-800"
          >
            <ArrowLeft className="h-4 w-4" />
            All campaigns
          </Link>
          <div className="mb-1 flex items-center gap-2.5">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-600">
              <Megaphone className="h-4 w-4 text-white" />
            </div>
            <h1 className="text-2xl font-black text-gray-900">
              {isEdit ? 'Edit draft' : 'New campaign'}
            </h1>
          </div>
          <p className="text-sm text-gray-500">
            Saved as draft. Start or schedule from the campaign detail page.
          </p>
        </div>

        <div className="space-y-4 rounded-xl border border-gray-100 bg-white p-6 shadow-sm">
          <div>
            <label className="mb-1 block text-xs text-gray-500">Name</label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="July re-engagement"
            />
          </div>

          <div>
            <label className="mb-1 block text-xs text-gray-500">Channels</label>
            <div className="flex flex-wrap gap-2">
              {CHANNELS.map((ch) => (
                <button
                  key={ch.id}
                  type="button"
                  onClick={() => toggleChannel(ch.id)}
                  className={`rounded-md border px-2.5 py-1 text-xs ${
                    channels.includes(ch.id)
                      ? 'border-blue-200 bg-blue-50 text-blue-800'
                      : 'border-gray-200 bg-white text-gray-500'
                  }`}
                >
                  {ch.label}
                </button>
              ))}
            </div>
          </div>

          {channels.includes('email') && (
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <label className="mb-1 block text-xs text-gray-500">From</label>
                <select
                  value={fromEmail}
                  onChange={(e) => setFromEmail(e.target.value)}
                  className="h-10 w-full rounded-md border border-gray-300 bg-white px-3 text-sm"
                >
                  {mailboxes.map((m) => (
                    <option key={m.email} value={m.email}>
                      {m.name} &lt;{m.email}&gt;
                    </option>
                  ))}
                  <option value="">FeelyFeely &lt;update@feelyfeely.com&gt; (no replies)</option>
                </select>
                <p className="mt-1 text-xs text-gray-500">
                  {fromEmail
                    ? 'Replies come back to this mailbox and show in Communications.'
                    : 'The notifications address. Replies are not collected anywhere.'}
                </p>
              </div>
              {fromEmail && (
                <label className="flex items-center gap-2 self-center text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={addSignature}
                    onChange={(e) => setAddSignature(e.target.checked)}
                  />
                  Add this mailbox&rsquo;s signature
                  {!mailboxes.find((m) => m.email === fromEmail)?.signature_html && (
                    <span className="text-xs text-gray-400">(none set yet)</span>
                  )}
                </label>
              )}
            </div>
          )}

          {(channels.includes('email') || channels.includes('in_app')) && (
            <div>
              <label className="mb-1 block text-xs text-gray-500">Subject</label>
              <input
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                placeholder="We miss you"
              />
            </div>
          )}

          <div>
            <label className="mb-1 block text-xs text-gray-500">
              Body{' '}
              <span className="text-gray-400">
                (placeholders: {'{{first_name}}'}, {'{{email}}'}, uploaded columns; add a fallback with{' '}
                {'{{company|your business}}'})
              </span>
            </label>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={4}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
              placeholder="Hi, come see what's new."
            />
          </div>

          {channels.includes('email') && (
            <div className="space-y-2">
              <div className="flex flex-wrap items-end justify-between gap-2">
                <label className="block text-xs text-gray-500">HTML (optional)</label>
                <select
                  aria-label="Start from a template"
                  value=""
                  onChange={(e) => {
                    const t = emailTemplates.find((x) => String(x.ntmplid) === e.target.value);
                    if (!t) return;
                    if ((html.trim() || body.trim()) && !window.confirm('Replace the current HTML and body with this template?')) return;
                    setHtml(t.email_html ?? '');
                    setBody(t.email_text ?? '');
                    toast.success(`Loaded template #${t.ntmplid}`, { richColors: true });
                  }}
                  className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700"
                >
                  <option value="">Start from a template…</option>
                  {emailTemplates.map((t) => (
                    <option key={t.ntmplid} value={t.ntmplid}>
                      #{t.ntmplid} {t.name || t.name_system}
                    </option>
                  ))}
                </select>
              </div>
              <textarea
                value={html}
                onChange={(e) => setHtml(e.target.value)}
                rows={html ? 10 : 3}
                spellCheck={false}
                className="w-full rounded-md border border-gray-300 px-3 py-2 font-mono text-xs"
              />
              {html.trim() && <EmailFrame html={html} height="h-[640px]" />}
            </div>
          )}

          {channels.includes('whatsapp') && (
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <label className="mb-1 block text-xs text-gray-500">
                  WhatsApp template (Meta-approved)
                </label>
                <input
                  value={waTemplate}
                  onChange={(e) => setWaTemplate(e.target.value)}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                  placeholder="reengage_v2"
                />
              </div>
              <div>
                <label className="mb-1 block text-xs text-gray-500">Language</label>
                <input
                  value={waLanguage}
                  onChange={(e) => setWaLanguage(e.target.value)}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                />
              </div>
            </div>
          )}

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label className="block text-xs text-gray-500">Audience</label>
              <a
                href="/admin/relationship/segments/new"
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 text-xs font-medium text-blue-700 hover:underline"
              >
                + New segment
              </a>
            </div>
            {segmentsQ.isLoading ? (
              <Skeleton className="h-10 w-full" />
            ) : (
              <div className="grid gap-3 sm:grid-cols-2">
                <MultiSelectFilter
                  label="Send to segments"
                  searchable
                  capitalize={false}
                  anyLabel={segments.length ? 'Choose segments' : 'No segments yet'}
                  triggerClassName="w-full"
                  options={segmentOptions}
                  value={segmentIds}
                  onChange={setSegmentIds}
                />
                <MultiSelectFilter
                  label="Leave out anyone in"
                  searchable
                  capitalize={false}
                  anyLabel="Nobody left out"
                  triggerClassName="w-full"
                  options={segmentOptions.filter((o) => !segmentIds.includes(o.key))}
                  value={excludeIds}
                  onChange={setExcludeIds}
                />
              </div>
            )}
            <p className="text-xs text-gray-500">
              People in several chosen segments get the message once. A new segment opens in a new tab; this list
              refreshes when you come back.
            </p>
          </div>

          <div>
            <label className="mb-1 block text-xs text-gray-500">
              Ad-hoc recipients (uid:N · email · +phone)
            </label>
            <textarea
              value={adHoc}
              onChange={(e) => setAdHoc(e.target.value)}
              rows={3}
              className="w-full rounded-md border border-gray-300 px-3 py-2 font-mono text-sm"
              placeholder={'uid:102\nada@example.com\n+233201234567'}
            />
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={respectPrefs}
                onChange={(e) => setRespectPrefs(e.target.checked)}
                className="rounded border-gray-300"
              />
              Respect opt-outs / prefs
            </label>
            <div>
              <label className="mb-1 block text-xs text-gray-500">Rate limit / min</label>
              <input
                type="number"
                value={rateLimit}
                onChange={(e) => setRateLimit(e.target.value)}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
              />
            </div>
          </div>

          <div className="flex justify-end gap-2">
            <Button asChild variant="outline">
              <Link to="/admin/relationship/campaigns">Cancel</Link>
            </Button>
            <Button disabled={saving} onClick={handleSave} className="gap-2">
              {saving && <Loader2 className="h-4 w-4 animate-spin" />}
              {isEdit ? 'Save draft' : 'Create draft'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CampaignEditorPage;
